import { useEffect, useState } from "react";
import { RefinementList } from "react-instantsearch-dom";

import CollapsiblePanel from "components/SearchFilters/components/CollapsiblePanel";
import CustomSearchBox from "components/SearchFilters/components/CustomSearchBox";
import CustomRefinementList from "components/SearchFilters/components/RefinementList";
import HierarchicalSkillFilter from "components/SearchFilters/components/HierarchicalSkillFilter";

import CustomCheckboxRefinement from "./CustomToggleRefinement";
import HelpText from "components/HelpText";

import "./index.css";

const hasRefinementListValue = (searchState, attribute) => {
  const values = searchState?.refinementList?.[attribute];
  return Array.isArray(values) && values.length > 0;
};

const isFilterActive = {
  textSearch: (searchState, query) => Boolean(query?.trim()),
  matcher: (searchState) =>
    hasRefinementListValue(searchState, "torcOwner.username") ||
    searchState?.toggle?.hasMatcher !== undefined,
  status: (searchState) => hasRefinementListValue(searchState, "status"),
  dmp: (searchState) => searchState?.toggle?.isDMP === true,
  customer: (searchState) =>
    hasRefinementListValue(searchState, "organization"),
  region: (searchState) =>
    hasRefinementListValue(searchState, "geographicalRegions.regionName"),
  requiredSkills: (searchState) =>
    hasRefinementListValue(searchState, "TKSkillsHierarchy"),
  optionalSkills: (searchState) =>
    hasRefinementListValue(searchState, "optionalTKSkillsHierarchy"),
};

export default function JobSearchFilters({
  searchState,
  showCustomerFilter,
  onSearchInputChange,
  query = "",
}) {
  const [textSearchValue, setTextSearchValue] = useState(query);
  const matcherLimit =
    searchState?.refinementList?.["torcOwner.username"]?.length || 1;

  useEffect(() => {
    setTextSearchValue(query);
  }, [query]);

  return (
    <div className="job-search-filters" data-testid="jobs-search-filters">
      <div id="filter-group-text-search">
        <CollapsiblePanel
          header="Text Search"
          sectionKey="text-search"
          defaultCollapsed={!isFilterActive.textSearch(searchState, query)}
        >
          <CustomSearchBox
            id="job-text-search"
            placeholder="Find by job id or title"
            value={textSearchValue}
            onChange={(event) => {
              setTextSearchValue(event.target.value);
              onSearchInputChange(event);
            }}
            onClear={() => {
              setTextSearchValue("");
              onSearchInputChange({ target: { value: "" } });
            }}
          />
        </CollapsiblePanel>
      </div>

      <div id="filter-group-matcher">
        <CollapsiblePanel
          header="Matcher"
          sectionKey="matcher"
          defaultCollapsed={!isFilterActive.matcher(searchState)}
        >
          {!searchState?.toggle?.["hasMatcher"] && (
            <CustomRefinementList
              placeholder="Find more matchers"
              attribute="torcOwner.username"
              limit={matcherLimit}
              showMore
              searchable
              transformItems={(items) =>
                items.sort((a, b) => b.isRefined - a.isRefined)
              }
            />
          )}

          {!searchState?.refinementList?.["torcOwner.username"]?.length && (
            <CustomCheckboxRefinement
              className="hidden"
              attribute="hasMatcher"
              label="No Matcher"
              value={false}
            />
          )}
        </CollapsiblePanel>
      </div>

      <div id="filter-group-status">
        <CollapsiblePanel
          header="Status"
          sectionKey="status"
          defaultCollapsed={!isFilterActive.status(searchState)}
        >
          <RefinementList
            attribute="status"
            limit={2}
            showMore
            operator="or"
            transformItems={(items) =>
              items.sort((a, b) => a.label.localeCompare(b.label))
            }
          />
        </CollapsiblePanel>
      </div>

      <div id="filter-group-dmp">
        <CollapsiblePanel
          header="DMP"
          sectionKey="dmp"
          defaultCollapsed={!isFilterActive.dmp(searchState)}
          headerSuffix={
            <HelpText
              text="Shows only jobs that came from the DMP (self-service) platform"
              iconType="info"
            />
          }
        >
          <CustomCheckboxRefinement
            attribute="isDMP"
            label="DMP Jobs Only"
            value={true}
          />
        </CollapsiblePanel>
      </div>

      {showCustomerFilter && (
        <div id="filter-group-customer">
          <CollapsiblePanel
            header="Customer"
            sectionKey="customer"
            defaultCollapsed={!isFilterActive.customer(searchState)}
          >
            <CustomRefinementList
              placeholder="Find more customers"
              attribute="organization"
              limit={10}
              showMore
              searchable
            />
          </CollapsiblePanel>
        </div>
      )}

      <div id="filter-group-region">
        <CollapsiblePanel
          header="Region"
          sectionKey="region"
          defaultCollapsed={!isFilterActive.region(searchState)}
        >
          <RefinementList
            attribute="geographicalRegions.regionName"
            limit={20}
          />
        </CollapsiblePanel>
      </div>

      <div id="filter-group-required-skills">
        <CollapsiblePanel
          header="Required Skills"
          sectionKey="required-skills"
          defaultCollapsed={!isFilterActive.requiredSkills(searchState)}
        >
          <HierarchicalSkillFilter
            attribute="TKSkillsHierarchy"
            placeholder="Find more skills"
            limit={10}
            showMoreLimit={1000}
            searchable
            showMore
          />
        </CollapsiblePanel>
      </div>

      <div id="filter-group-optional-skills">
        <CollapsiblePanel
          header="Optional Skills"
          sectionKey="optional-skills"
          defaultCollapsed={!isFilterActive.optionalSkills(searchState)}
        >
          <HierarchicalSkillFilter
            attribute="optionalTKSkillsHierarchy"
            placeholder="Find more skills"
            limit={10}
            showMoreLimit={1000}
            searchable
            showMore
          />
        </CollapsiblePanel>
      </div>
    </div>
  );
}
